'use client';

import { useEffect, useState } from 'react';
import { Users } from 'lucide-react';
import NumberFlow from '@number-flow/react';
import { apiFetch } from '@/lib/api';
import DashboardMetricCard from '../DashboardMetricCard';

type MyFollow = {
  personId: number;
  followerCount: number;
  followingCount: number;
  newFollowersLast7d?: number;
};

export default function FollowersCard() {
  const [snap, setSnap] = useState<MyFollow | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch('/api/proxy/api/follow/me', undefined, { method: 'GET' });
        if (!res.ok) {
          if (!cancelled) setError(true);
          return;
        }
        const data = (await res.json()) as MyFollow;
        if (!cancelled) setSnap(data);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const icon = <Users className="h-4 w-4" strokeWidth={1.75} />;

  if (error) {
    return (
      <DashboardMetricCard title="Followers" icon={icon}>
        <div className="text-sm text-muted-foreground">Couldn&apos;t load followers right now.</div>
      </DashboardMetricCard>
    );
  }

  if (snap === null) {
    return (
      <DashboardMetricCard title="Followers" href="/profile/followers" icon={icon}>
        <div className="space-y-3">
          <div className="h-9 w-20 rounded bg-muted animate-pulse" />
          <div className="h-3 w-28 rounded bg-muted/60 animate-pulse" />
        </div>
      </DashboardMetricCard>
    );
  }

  const total = snap.followerCount ?? 0;
  const last7 = snap.newFollowersLast7d ?? 0;

  if (total === 0) {
    return (
      <DashboardMetricCard title="Followers" icon={icon}>
        <div className="font-serif text-3xl font-semibold tabular-nums">0</div>
        <div className="text-sm text-muted-foreground mt-1">
          Earn your first by completing your profile or posting.
        </div>
      </DashboardMetricCard>
    );
  }

  return (
    <DashboardMetricCard title="Followers" href="/profile/followers" icon={icon}>
      <div className="font-serif text-3xl font-semibold tabular-nums">
        <NumberFlow value={total} />
      </div>
      <div className="text-sm mt-1">
        {last7 > 0 ? (
          <>
            <span className="font-medium text-success">+{last7}</span>{' '}
            <span className="text-muted-foreground">this week</span>
          </>
        ) : (
          <span className="text-muted-foreground">{total === 1 ? 'Total follower' : 'Total followers'}</span>
        )}
      </div>
    </DashboardMetricCard>
  );
}
